import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ToastBar, Toaster } from "react-hot-toast";
import "./AppToaster.scss";

const APP_TOASTER_Z_INDEX = 9_999_998;

// react-hot-toast's toast enter animation lasts 0.35s (icon animations finish
// even sooner). Toasts older than that have already animated in; when the
// portal target changes (a dialog opens or closes) the toast DOM is
// recreated, which would replay the entrance animations of the toast body
// and its status icon. Marking settled toasts with `is-settled` lets
// AppToaster.scss jump those animations to their final frame. The dismiss
// timer is unaffected either way: remaining time is derived from the toast's
// createdAt, which survives the remount.
const TOAST_ENTER_ANIMATION_MS = 350;

function getOpenDialogs() {
    return Array.from(
        document.querySelectorAll<HTMLDialogElement>("dialog[open]"),
    );
}

function getTopDialog(dialogStack: readonly HTMLDialogElement[]) {
    for (let index = dialogStack.length - 1; index >= 0; index -= 1) {
        const dialog = dialogStack[index];
        if (dialog?.isConnected && dialog.open) return dialog;
    }

    return null;
}

export function AppToaster() {
    const [portalTarget, setPortalTarget] = useState<HTMLElement>(
        () => document.body,
    );

    useEffect(() => {
        let dialogStack = getOpenDialogs();

        const updatePortalTarget = () => {
            const openDialogs = getOpenDialogs();
            const openDialogSet = new Set(openDialogs);

            dialogStack = dialogStack.filter((dialog) =>
                openDialogSet.has(dialog),
            );

            for (const dialog of openDialogs) {
                if (!dialogStack.includes(dialog)) dialogStack.push(dialog);
            }

            setPortalTarget(getTopDialog(dialogStack) ?? document.body);
        };

        updatePortalTarget();

        const observer = new MutationObserver((records) => {
            for (const record of records) {
                if (
                    record.type !== "attributes" ||
                    !(record.target instanceof HTMLDialogElement)
                ) {
                    continue;
                }

                const dialog = record.target;
                dialogStack = dialogStack.filter(
                    (stackedDialog) => stackedDialog !== dialog,
                );

                if (dialog.open) dialogStack.push(dialog);
            }

            updatePortalTarget();
        });

        observer.observe(document.body, {
            attributeFilter: ["open"],
            attributes: true,
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, []);

    return createPortal(
        <Toaster
            position="top-center"
            containerClassName="app-toaster"
            containerStyle={{
                top: "calc(var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 0px) + 16px)",
                // Eruda's root uses 9,999,999.
                zIndex: APP_TOASTER_Z_INDEX,
            }}
        >
            {(t) => {
                const hasSettled =
                    t.visible &&
                    Date.now() - t.createdAt > TOAST_ENTER_ANIMATION_MS;

                return (
                    <div
                        className={[
                            "app-toaster__toast",
                            hasSettled ? "is-settled" : null,
                        ]
                            .filter(Boolean)
                            .join(" ")}
                    >
                        <ToastBar toast={t} />
                    </div>
                );
            }}
        </Toaster>,
        portalTarget,
    );
}
